// Funções compartilhadas pela tela do chat interno e pelo popover da barra
// superior. Sem dependência de React: só dados.

// Tons escuros o bastante para texto branco (avatar) e para nome sobre balão
// claro. No tema escuro o nome usa a versão clara (NAME_COLORS_DARK).
const USER_COLORS = [
  "#7A3FC2", // roxo
  "#0F7B6C", // teal
  "#B0306A", // rosa
  "#B4530A", // laranja
  "#2468D4", // azul
  "#3D5A80", // azul acinzentado
  "#157A47", // verde
  "#8A5A00", // âmbar
];

const NAME_COLORS_DARK = [
  "#C9A6F5",
  "#6FD3C2",
  "#F29AC2",
  "#F5B27A",
  "#8DB8F5",
  "#A9C1E0",
  "#7FD6A6",
  "#E8C26A",
];

export const userColor = (id) =>
  USER_COLORS[Math.abs(+id || 0) % USER_COLORS.length];

export const userNameColor = (id, isDark) =>
  isDark
    ? NAME_COLORS_DARK[Math.abs(+id || 0) % NAME_COLORS_DARK.length]
    : userColor(id);

export const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Participantes além do usuário logado
export const otherMembers = (chat, userId) =>
  (chat?.users || []).filter((u) => u.userId !== userId);

// Conversa direta = sem título e com exatamente 2 participantes. Não há
// coluna no banco para isso; o modal cria diretas com título vazio.
export const isDirectChat = (chat) =>
  !!chat &&
  !(chat.title || "").trim() &&
  Array.isArray(chat.users) &&
  chat.users.length === 2;

export const getChatTitle = (chat, userId) => {
  if (!chat) return "";
  const title = (chat.title || "").trim();
  if (title) return title;
  const names = otherMembers(chat, userId)
    .map((u) => u.user?.name)
    .filter(Boolean);
  return names.length ? names.join(", ") : "Conversa";
};

export const unreadsFor = (chat, userId) => {
  const me = (chat?.users || []).find((u) => u.userId === userId);
  return me?.unreads || 0;
};

// O backend grava lastMessage como "Nome: texto"
export const formatPreview = (chat, user) => {
  const last = chat?.lastMessage || "";
  if (!last) return "Sem mensagens ainda";
  if (user?.name && last.startsWith(`${user.name}: `)) {
    return `Você: ${last.slice(user.name.length + 2)}`;
  }
  if (isDirectChat(chat)) {
    const other = otherMembers(chat, user?.id)[0]?.user?.name;
    if (other && last.startsWith(`${other}: `)) {
      return last.slice(other.length + 2);
    }
  }
  return last;
};

// Os eventos de socket nem sempre trazem os dados do usuário de cada
// participante; mantém o que já tínhamos para não perder nomes na tela.
export const mergeChat = (prev, incoming) => {
  if (!prev) return incoming;
  if (!incoming) return prev;
  const users = Array.isArray(incoming.users)
    ? incoming.users.map((u) => {
        const old = (prev.users || []).find((p) => p.userId === u.userId);
        return { ...old, ...u, user: u.user || old?.user };
      })
    : prev.users;
  return { ...prev, ...incoming, users };
};

export const sortChats = (chats) =>
  [...chats].sort(
    (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
  );

// Junta listas de mensagens sem repetir e em ordem de envio
export const mergeMessages = (a, b) => {
  const map = new Map();
  [...a, ...b].forEach((m) => {
    if (m && m.id != null) map.set(m.id, m);
  });
  return [...map.values()].sort((x, y) => x.id - y.id);
};

const WEEKDAYS = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];
const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

const pad = (n) => String(n).padStart(2, "0");

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const daysAgo = (date) =>
  Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

export const isSameDay = (a, b) => {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
};

export const formatTime = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Horário curto da lista: 14:32 · Ontem · terça · 22/10 · 22/10/2025
export const formatListTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  const diff = daysAgo(d);
  if (diff <= 0) return formatTime(d);
  if (diff === 1) return "Ontem";
  if (diff < 7) return WEEKDAYS[d.getDay()].replace("-feira", "");
  if (d.getFullYear() === new Date().getFullYear()) {
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
  }
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// Separador de dia na conversa
export const formatDayLabel = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return "";
  const diff = daysAgo(d);
  if (diff <= 0) return "Hoje";
  if (diff === 1) return "Ontem";
  const base = `${d.getDate()} de ${MONTHS[d.getMonth()]}`;
  if (diff < 7) return `${WEEKDAYS[d.getDay()]}, ${base}`;
  if (d.getFullYear() === new Date().getFullYear()) return base;
  return `${base} de ${d.getFullYear()}`;
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Quebra o texto em pedaços: texto, @menção (de alguém da conversa) e link
export const splitMessage = (text, names = []) => {
  if (!text) return [];
  const sorted = [...new Set(names.filter(Boolean))].sort(
    (a, b) => b.length - a.length
  );
  const parts = ["https?:\\/\\/[^\\s<]+[^\\s<.,;:!?)\\]'\"]"];
  if (sorted.length) {
    parts.unshift(`@(?:${sorted.map(escapeRegExp).join("|")})(?![\\wÀ-ÿ])`);
  }
  const re = new RegExp(parts.join("|"), "gi");
  const out = [];
  let last = 0;
  let match;
  while ((match = re.exec(text))) {
    if (match.index > last) {
      out.push({ type: "text", value: text.slice(last, match.index) });
    }
    const value = match[0];
    out.push({ type: value.startsWith("@") ? "mention" : "link", value });
    last = match.index + value.length;
  }
  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out;
};

export const mentionsUser = (text, name) =>
  !!text &&
  !!name &&
  new RegExp(`@${escapeRegExp(name)}(?![\\wÀ-ÿ])`, "i").test(text);

// Última mensagem da conversa cita a pessoa (para o "@" na lista)
export const lastMessageMentions = (chat, user) =>
  mentionsUser(chat?.lastMessage, user?.name);

// Quem (fora o remetente) já leu a mensagem
export const readersOf = (chat, message) => {
  const sentAt = new Date(message.createdAt).getTime();
  return (chat?.users || []).filter(
    (u) =>
      u.userId !== message.senderId &&
      u.lastReadAt &&
      new Date(u.lastReadAt).getTime() >= sentAt
  );
};

export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

export const CHAT_MAX_FILE_SIZE = 20 * 1024 * 1024;

export const TICKET_STATUS_LABEL = {
  open: "Em atendimento",
  pending: "Aguardando",
  closed: "Finalizado",
};
